import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Plus, Trash2, Save, BookOpen, FileSpreadsheet, Edit2, X, Users, Crown, Mail, Send, TestTube, Bell, CheckCircle2, XCircle, Clock, FileText, Settings, Timer, Percent, Zap, Download, History, Tag, Video, Upload } from "lucide-react";
import { toast } from "sonner";
import PromotionsModule from "../components/admin/PromotionsModule";
import MuxVideoLibrary from "../components/admin/MuxVideoLibrary";
import { resolveImageUrl } from "../lib/images";

const AdminDashboard = () => {
  const { API } = useAuth();
  const [activeTab, setActiveTab] = useState("courses");
  
  // Course State
  const [course, setCourse] = useState({
    title: "",
    description: "",
    category: "Contabilidad",
    level: "Básico",
    tier: "GRATIS",
    image_url: "",
    duration_hours: 0,
    order: 0,
    what_you_will_learn: [""],
    requirements: [""],
    modules: [],
    course_materials: [],
  });
  const [courseLoading, setCourseLoading] = useState(false);
  const [coursesList, setCoursesList] = useState([]);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showCourseForm, setShowCourseForm] = useState(false);

  // Templates State
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState({
    title: "",
    description: "",
    category: "Contabilidad",
    type: "excel",
    url: "",
    order: 0,
  });
  const [templateLoading, setTemplateLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Settings State
  const [settings, setSettings] = useState({ monthly_template_download_limit: 5 });
  const [muxVideos, setMuxVideos] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageInfo, setImageInfo] = useState(null);

  const uploadCourseImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImageUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/admin/course-image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCourse((c) => ({ ...c, image_url: res.data.url }));
      setImageInfo(res.data);
      toast.success(`Imagen optimizada: ${res.data.size_kb} KB`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir la imagen");
    } finally {
      setImageUploading(false);
    }
  };

  // Template downloads quota state
  const [usageData, setUsageData] = useState({ global_limit: 5, month: "", users: [] });
  const [usageLoading, setUsageLoading] = useState(false);
  const [quotaEdits, setQuotaEdits] = useState({});
  const [quotaSavingId, setQuotaSavingId] = useState(null);
  const [downloadHistory, setDownloadHistory] = useState([]);
  const [historyUserId, setHistoryUserId] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Promotion State (Timer de Descuento)
  const [promotion, setPromotion] = useState({
    active: false,
    title: "¡Oferta por tiempo limitado!",
    description: "Aprovecha este descuento exclusivo antes de que termine.",
    discount_percentage: 20,
    applies_to_monthly: true,
    applies_to_annual: true,
    time_mode: "hours",
    duration_hours: 24,
    starts_at: null,
    ends_at: null,
    is_live: false,
    seconds_left: 0,
  });
  const [promotionLoading, setPromotionLoading] = useState(false);

  // Subscriptions State
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);

  // Email Campaign State
  const [campaigns, setCampaigns] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [campaignSubject, setCampaignSubject] = useState("");
  const [campaignBody, setCampaignBody] = useState("");
  const [campaignSegment, setCampaignSegment] = useState("all");
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailLogFilter, setEmailLogFilter] = useState("");
  const editorRef = useRef(null);

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Load subscriptions when tab changes
  useEffect(() => {
    if (activeTab === "courses") {
      loadCoursesList();
    }
    if (activeTab === "subscriptions") {
      loadSubscriptions();
    }
    if (activeTab === "email") {
      loadCampaigns();
      loadEmailLogs();
    }
    if (activeTab === "settings") {
      loadSettings();
      loadPromotion();
    }
    if (activeTab === "downloads") {
      loadTemplateUsage();
      loadDownloadHistory(null);
    }
    if (activeTab === "courses") {
      loadMuxVideos();
    }
  }, [activeTab]);

  const loadMuxVideos = async () => {
    try {
      const res = await axios.get(`${API}/admin/mux/videos`);
      setMuxVideos(res.data.videos || []);
    } catch (e) {
      setMuxVideos([]);
    }
  };

  const loadTemplateUsage = async () => {
    setUsageLoading(true);
    try {
      const res = await axios.get(`${API}/admin/template-usage`);
      setUsageData(res.data);
      const edits = {};
      res.data.users.forEach((u) => {
        edits[u.user_id] = {
          limit_override: u.limit_override === null || u.limit_override === undefined ? "" : String(u.limit_override),
          extra_downloads: String(u.extra_downloads || 0),
        };
      });
      setQuotaEdits(edits);
    } catch (error) {
      toast.error("Error al cargar el consumo de plantillas");
    } finally {
      setUsageLoading(false);
    }
  };

  const loadDownloadHistory = async (userId) => {
    try {
      const url = userId ? `${API}/admin/template-downloads?user_id=${userId}` : `${API}/admin/template-downloads`;
      const res = await axios.get(url);
      setDownloadHistory(res.data);
      setHistoryUserId(userId);
    } catch (error) {
      console.error("Error loading download history:", error);
    }
  };

  const saveQuota = async (userId) => {
    const edit = quotaEdits[userId] || {};
    setQuotaSavingId(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/template-quota`, {
        limit_override: edit.limit_override === "" ? null : parseInt(edit.limit_override),
        extra_downloads: parseInt(edit.extra_downloads) || 0,
      });
      toast.success("Cupo actualizado");
      loadTemplateUsage();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el cupo");
    } finally {
      setQuotaSavingId(null);
    }
  };

  const loadCoursesList = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCoursesList(response.data);
    } catch (error) {
      console.error("Error loading courses:", error);
    }
  };

  const resetCourseForm = () => {
    setEditingCourse(null);
    setShowCourseForm(false);
    setCourse({
      title: "",
      description: "",
      category: "Contabilidad",
      level: "Básico",
      tier: "GRATIS",
      image_url: "",
      duration_hours: 0,
      order: 0,
      what_you_will_learn: [""],
      requirements: [""],
      modules: [],
      course_materials: [],
    });
  };

  const editCourse = (c) => {
    setEditingCourse(c);
    setShowCourseForm(true);
    setCourse({
      title: c.title || "",
      description: c.description || "",
      category: c.category || "Contabilidad",
      level: c.level || "Básico",
      tier: c.tier || "GRATIS",
      image_url: c.image_url || "",
      duration_hours: c.duration_hours || 0,
      order: c.order || 0,
      what_you_will_learn: c.what_you_will_learn?.length ? c.what_you_will_learn : [""],
      requirements: c.requirements?.length ? c.requirements : [""],
      modules: c.modules || [],
      course_materials: c.course_materials || [],
    });
    toast.success(`Editando: ${c.title}`);
    // Wait for the form to render, then scroll to it
    setTimeout(() => {
      const formEl = document.getElementById("course-edit-form");
      if (formEl) {
        formEl.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      }
    }, 150);
  };

  const deleteCourse = async (courseId, courseTitle) => {
    if (!window.confirm(`¿Eliminar el curso "${courseTitle}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/courses/${courseId}`);
      toast.success("Curso eliminado");
      loadCoursesList();
    } catch (error) {
      toast.error("Error al eliminar curso");
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error loading settings:", error);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await axios.put(`${API}/admin/settings`, {
        monthly_template_download_limit: parseInt(settings.monthly_template_download_limit) || 0,
      });
      setSettings(response.data);
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadPromotion = async () => {
    try {
      const response = await axios.get(`${API}/admin/promotion`);
      const data = response.data;
      // Formatea fechas ISO para inputs datetime-local (YYYY-MM-DDTHH:mm)
      const toLocalInput = (iso) => {
        if (!iso) return "";
        try {
          const d = new Date(iso);
          const pad = (n) => String(n).padStart(2, "0");
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        } catch {
          return "";
        }
      };
      setPromotion({
        ...data,
        starts_at_local: toLocalInput(data.starts_at),
        ends_at_local: toLocalInput(data.ends_at),
      });
    } catch (error) {
      console.error("Error loading promotion:", error);
    }
  };

  const handleSavePromotion = async () => {
    setPromotionLoading(true);
    try {
      const payload = {
        active: !!promotion.active,
        title: promotion.title || "",
        description: promotion.description || "",
        discount_percentage: parseInt(promotion.discount_percentage) || 0,
        applies_to_monthly: !!promotion.applies_to_monthly,
        applies_to_annual: !!promotion.applies_to_annual,
        time_mode: promotion.time_mode || "hours",
        duration_hours: parseInt(promotion.duration_hours) || 24,
        starts_at: promotion.time_mode === "dates" && promotion.starts_at_local
          ? new Date(promotion.starts_at_local).toISOString()
          : null,
        ends_at: promotion.time_mode === "dates" && promotion.ends_at_local
          ? new Date(promotion.ends_at_local).toISOString()
          : null,
      };
      const response = await axios.put(`${API}/admin/promotion`, payload);
      toast.success(response.data.is_live ? "Promoción guardada y activa" : "Promoción guardada");
      await loadPromotion();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar la promoción");
    } finally {
      setPromotionLoading(false);
    }
  };

  const handleTogglePromotion = async () => {
    setPromotionLoading(true);
    try {
      const response = await axios.post(`${API}/admin/promotion/toggle`);
      toast.success(response.data.active ? "Promoción activada" : "Promoción desactivada");
      await loadPromotion();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Configura la promoción primero");
    } finally {
      setPromotionLoading(false);
    }
  };

  const formatCountdown = (secs) => {
    if (!secs || secs <= 0) return "00:00:00";
    const d = Math.floor(secs / 86400);
    const h = Math.floor((secs % 86400) / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  // Reload email logs when filter changes
  useEffect(() => {
    if (activeTab === "email") {
      loadEmailLogs();
    }
  }, [emailLogFilter]);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error loading templates:", error);
    }
  };

  const loadSubscriptions = async () => {
    setSubscriptionsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/subscriptions`);
      setSubscriptions(response.data);
    } catch (error) {
      console.error("Error loading subscriptions:", error);
      toast.error("Error al cargar suscripciones");
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  // Email Functions
  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/admin/email/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error("Error loading campaigns:", error);
    }
  };

  const loadEmailLogs = async () => {
    setEmailLogsLoading(true);
    try {
      const url = emailLogFilter && emailLogFilter !== "all_types" ? `${API}/admin/email/logs?email_type=${emailLogFilter}` : `${API}/admin/email/logs`;
      const response = await axios.get(url);
      setEmailLogs(response.data);
    } catch (error) {
      console.error("Error loading email logs:", error);
    } finally {
      setEmailLogsLoading(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!campaignSubject.trim() || !campaignBody.trim()) {
      toast.error("Completa el asunto y contenido del email");
      return;
    }
    if (!window.confirm(`¿Enviar campana "${campaignSubject}" a ${campaignSegment === "all" ? "todos los usuarios" : `usuarios ${campaignSegment.toUpperCase()}`}?`)) return;

    setCampaignLoading(true);
    try {
      const response = await axios.post(`${API}/admin/email/campaign`, {
        subject: campaignSubject,
        html_body: campaignBody,
        segment: campaignSegment,
      });
      toast.success(response.data.message);
      setCampaignSubject("");
      setCampaignBody("");
      setTimeout(() => loadCampaigns(), 3000);
    } catch (error) {
      toast.error("Error al enviar campana");
    } finally {
      setCampaignLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      const response = await axios.post(`${API}/admin/email/test`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Error al enviar email de prueba");
    }
  };

  const handleSendReminders = async () => {
    try {
      const response = await axios.post(`${API}/admin/email/send-reminder`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Error al enviar recordatorios");
    }
  };

  const insertFormatting = (tag) => {
    const textarea = editorRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = campaignBody.substring(start, end);
    let formatted = "";
    switch (tag) {
      case "bold": formatted = `<strong>${selected || "texto"}</strong>`; break;
      case "italic": formatted = `<em>${selected || "texto"}</em>`; break;
      case "h2": formatted = `<h2 style="color:#fff;font-size:20px;margin:16px 0 8px;">${selected || "Titulo"}</h2>`; break;
      case "link": formatted = `<a href="https://" style="color:#E50914;text-decoration:underline;">${selected || "enlace"}</a>`; break;
      case "button": formatted = `<table role="presentation" cellspacing="0" cellpadding="0" style="margin:16px 0;"><tr><td style="border-radius:8px;background:#E50914;"><a href="https://" style="display:inline-block;padding:12px 28px;font-size:14px;font-weight:700;color:#fff;text-decoration:none;">${selected || "Boton"}</a></td></tr></table>`; break;
      case "br": formatted = `<br/>`; break;
      default: formatted = selected;
    }
    const newBody = campaignBody.substring(0, start) + formatted + campaignBody.substring(end);
    setCampaignBody(newBody);
  };

  // Course Functions
  const addModule = () => {
    setCourse({
      ...course,
      modules: [
        ...course.modules,
        { id: `temp-${Date.now()}`, title: "", order: course.modules.length + 1, lessons: [] },
      ],
    });
  };

  const updateModule = (index, field, value) => {
    const updatedModules = [...course.modules];
    updatedModules[index][field] = value;
    setCourse({ ...course, modules: updatedModules });
  };

  const removeModule = (index) => {
    const updatedModules = course.modules.filter((_, i) => i !== index);
    setCourse({ ...course, modules: updatedModules });
  };

  const addLesson = (moduleIndex) => {
    const updatedModules = [...course.modules];
    updatedModules[moduleIndex].lessons.push({
      id: `temp-${Date.now()}`,
      title: "",
      video_url: "",
      video_type: "youtube",
      duration_minutes: 0,
      order: updatedModules[moduleIndex].lessons.length + 1,
      materials: [],
    });
    setCourse({ ...course, modules: updatedModules });
  };

  const updateLesson = (moduleIndex, lessonIndex, field, value) => {
    const updatedModules = [...course.modules];
    updatedModules[moduleIndex].lessons[lessonIndex][field] = value;
    setCourse({ ...course, modules: updatedModules });
  };

  const removeLesson = (moduleIndex, lessonIndex) => {
    const updatedModules = [...course.modules];
    updatedModules[moduleIndex].lessons = updatedModules[moduleIndex].lessons.filter(
      (_, i) => i !== lessonIndex
    );
    setCourse({ ...course, modules: updatedModules });
  };

  // ===== Materiales generales del curso =====
  const addCourseMaterial = () => {
    setCourse({
      ...course,
      course_materials: [...(course.course_materials || []), { title: "", url: "", type: "pdf" }],
    });
  };

  const updateCourseMaterial = (index, field, value) => {
    const updated = [...(course.course_materials || [])];
    updated[index] = { ...updated[index], [field]: value };
    setCourse({ ...course, course_materials: updated });
  };

  const removeCourseMaterial = (index) => {
    setCourse({
      ...course,
      course_materials: (course.course_materials || []).filter((_, i) => i !== index),
    });
  };

  // ===== Materiales (PDFs, Excel, infografías) por lección =====
  const addMaterial = (moduleIndex, lessonIndex) => {
    const updatedModules = [...course.modules];
    const lesson = updatedModules[moduleIndex].lessons[lessonIndex];
    if (!lesson.materials) lesson.materials = [];
    lesson.materials.push({ title: "", url: "", type: "pdf" });
    setCourse({ ...course, modules: updatedModules });
  };

  const updateMaterial = (moduleIndex, lessonIndex, matIndex, field, value) => {
    const updatedModules = [...course.modules];
    updatedModules[moduleIndex].lessons[lessonIndex].materials[matIndex][field] = value;
    setCourse({ ...course, modules: updatedModules });
  };

  const removeMaterial = (moduleIndex, lessonIndex, matIndex) => {
    const updatedModules = [...course.modules];
    updatedModules[moduleIndex].lessons[lessonIndex].materials =
      updatedModules[moduleIndex].lessons[lessonIndex].materials.filter((_, i) => i !== matIndex);
    setCourse({ ...course, modules: updatedModules });
  };

  const addWhatYouWillLearn = () => {
    setCourse({ ...course, what_you_will_learn: [...course.what_you_will_learn, ""] });
  };

  const updateWhatYouWillLearn = (index, value) => {
    const updated = [...course.what_you_will_learn];
    updated[index] = value;
    setCourse({ ...course, what_you_will_learn: updated });
  };

  const removeWhatYouWillLearn = (index) => {
    const updated = course.what_you_will_learn.filter((_, i) => i !== index);
    setCourse({ ...course, what_you_will_learn: updated });
  };

  const addRequirement = () => {
    setCourse({ ...course, requirements: [...course.requirements, ""] });
  };

  const updateRequirement = (index, value) => {
    const updated = [...course.requirements];
    updated[index] = value;
    setCourse({ ...course, requirements: updated });
  };

  const removeRequirement = (index) => {
    const updated = course.requirements.filter((_, i) => i !== index);
    setCourse({ ...course, requirements: updated });
  };

  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    setCourseLoading(true);

    try {
      const cleanedCourse = {
        ...course,
        what_you_will_learn: course.what_you_will_learn.filter((item) => item.trim()),
        requirements: course.requirements.filter((item) => item.trim()),
      };

      if (editingCourse) {
        await axios.put(`${API}/courses/${editingCourse.id}`, cleanedCourse);
        toast.success("¡Curso actualizado exitosamente!");
      } else {
        await axios.post(`${API}/courses`, cleanedCourse);
        toast.success("¡Curso creado exitosamente!");
      }

      resetCourseForm();
      loadCoursesList();
    } catch (error) {
      toast.error(editingCourse ? "Error al actualizar curso" : "Error al crear curso");
    } finally {
      setCourseLoading(false);
    }
  };

  // Template Functions
  const handleTemplateSubmit = async (e) => {
    e.preventDefault();
    setTemplateLoading(true);

    try {
      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.id}`, template);
        toast.success("¡Plantilla actualizada exitosamente!");
        setEditingTemplate(null);
      } else {
        await axios.post(`${API}/templates`, template);
        toast.success("¡Plantilla creada exitosamente!");
      }

      setTemplate({
        title: "",
        description: "",
        category: "Contabilidad",
        type: "excel",
        url: "",
        order: 0,
      });
      loadTemplates();
    } catch (error) {
      toast.error("Error al guardar plantilla");
    } finally {
      setTemplateLoading(false);
    }
  };

  const editTemplate = (t) => {
    setEditingTemplate(t);
    setTemplate({
      title: t.title,
      description: t.description,
      category: t.category,
      type: t.type,
      url: t.url,
      order: t.order || 0,
    });
  };

  const cancelEdit = () => {
    setEditingTemplate(null);
    setTemplate({
      title: "",
      description: "",
      category: "Contabilidad",
      type: "excel",
      url: "",
      order: 0,
    });
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta plantilla?")) return;
    
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      toast.success("Plantilla eliminada");
      loadTemplates();
    } catch (error) {
      toast.error("Error al eliminar plantilla");
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
        <h1 className="text-4xl md:text-5xl font-black text-white mb-8" data-testid="admin-title">
          Panel de Administración
        </h1>

        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <Button
            onClick={() => setActiveTab("courses")}
            variant={activeTab === "courses" ? "default" : "outline"}
            className={activeTab === "courses" 
              ? "bg-[#E50914] hover:bg-[#E50914]/90 text-white" 
              : "border-white/20 text-white hover:bg-white/10"}
            data-testid="tab-courses"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Cursos
          </Button>
          <Button
            onClick={() => setActiveTab("templates")}
            variant={activeTab === "templates" ? "default" : "outline"}
            className={activeTab === "templates" 
              ? "bg-[#E50914] hover:bg-[#E50914]/90 text-white" 
              : "border-white/20 text-white hover:bg-white/10"}
            data-testid="tab-templates"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Plantillas
          </Button>
          <Button
            onClick={() => setActiveTab("subscriptions")}
            variant={activeTab === "subscriptions" ? "default" : "outline"}
            className={activeTab === "subscriptions" 
              ? "bg-[#E50914] hover:bg-[#E50914]/90 text-white" 
              : "border-white/20 text-white hover:bg-white/10"}
            data-testid="tab-subscriptions"
          >
            <Users className="w-4 h-4 mr-2" />
            Suscriptores
          </Button>
          <Button
            onClick={() => setActiveTab("downloads")}
            variant={activeTab === "downloads" ? "default" : "outline"}
            className={activeTab === "downloads"
              ? "bg-[#E50914] hover:bg-[#E50914]/90 text-white"
              : "border-white/20 text-white hover:bg-white/10"}
            data-testid="tab-downloads"
          >
            <Download className="w-4 h-4 mr-2" />
            Descargas
          </Button>
          <Button
            onClick={() => setActiveTab("videos")}
            variant={activeTab === "videos" ? "default" : "outline"}
            className={activeTab === "videos"
              ? "bg-[#E50914] hover:bg-[#E50914]/90 text-white"
              : "border-white/20 text-white hover:bg-white/10"}
            data-testid="tab-videos"
          >
            <Video className="w-4 h-4 mr-2" />
            Videos
          </Button>
          <Button
            onClick={() => setActiveTab("promotions")}
            variant={activeTab === "promotions" ? "default" : "outline"}
            className={activeTab === "promotions"
              ? "bg-[#E50914] hover:bg-[#E50914]/90 text-white"
              : "border-white/20 text-white hover:bg-white/10"}
            data-testid="tab-promotions"
          >
            <Tag className="w-4 h-4 mr-2" />
            Promociones
          </Button>
          <Button
            onClick={() => setActiveTab("email")}
            variant={activeTab === "email" ? "default" : "outline"}
            className={activeTab === "email" 
              ? "bg-[#E50914] hover:bg-[#E50914]/90 text-white" 
              : "border-white/20 text-white hover:bg-white/10"}
            data-testid="tab-email"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email
          </Button>
          <Button
            onClick={() => setActiveTab("settings")}
            variant={activeTab === "settings" ? "default" : "outline"}
            className={activeTab === "settings" 
              ? "bg-[#E50914] hover:bg-[#E50914]/90 text-white" 
              : "border-white/20 text-white hover:bg-white/10"}
            data-testid="tab-settings"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configuración
          </Button>
        </div>

        {/* Courses Tab */}
        {activeTab === "courses" && (
          <div className="space-y-6">
            {/* Course List */}
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Cursos Existentes ({coursesList.length})</CardTitle>
                  <CardDescription className="text-white/60">
                    Edita o elimina los cursos cargados
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  onClick={() => {
                    resetCourseForm();
                    setShowCourseForm(true);
                    setTimeout(() => {
                      const formEl = document.getElementById("course-edit-form");
                      if (formEl) {
                        formEl.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }, 150);
                  }}
                  className="bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                  data-testid="new-course-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Curso
                </Button>
              </CardHeader>
              <CardContent>
                {coursesList.length === 0 ? (
                  <p className="text-white/60 text-sm italic text-center py-6">
                    No hay cursos creados. Click en &quot;Nuevo Curso&quot; para agregar el primero.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {coursesList.map((c) => {
                      const totalLessons = (c.modules || []).reduce(
                        (acc, m) => acc + (m.lessons?.length || 0), 0
                      );
                      const isIncomplete = !c.modules?.length || totalLessons === 0;
                      return (
                        <div
                          key={c.id}
                          className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 transition-colors"
                          data-testid={`course-row-${c.id}`}
                        >
                          <img
                            src={resolveImageUrl(c.image_url)}
                            alt={c.title}
                            width={64}
                            height={48}
                            loading="lazy"
                            className="w-16 h-12 object-cover rounded shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-semibold truncate">{c.title}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <Badge className="bg-white/10 text-white border-white/20 text-xs">
                                {c.category}
                              </Badge>
                              <Badge className={c.tier === "PRO"
                                ? "bg-[#E50914] text-white border-0 text-xs"
                                : "bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs"}>
                                {c.tier}
                              </Badge>
                              <span className="text-white/50 text-xs">
                                Orden: {c.order || 0}
                              </span>
                              <span className="text-white/50 text-xs">
                                {(c.modules || []).length} módulos · {totalLessons} lecciones
                              </span>
                              {isIncomplete && (
                                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs">
                                  ⚠️ Sin lecciones
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => editCourse(c)}
                              className="bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                              data-testid={`edit-course-${c.id}`}
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                              Editar
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => deleteCourse(c.id, c.title)}
                              className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                              data-testid={`delete-course-${c.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Course Form (visible only when creating or editing) */}
            {showCourseForm && (
            <form id="course-edit-form" onSubmit={handleCourseSubmit} className="space-y-6">
            <Card className="bg-card/50 backdrop-blur-md border-[#E50914]/30 shadow-[0_0_30px_rgba(229,9,20,0.15)]">
              <CardHeader className="flex flex-row items-start justify-between">
                <div>
                  <CardTitle className="text-white">
                    {editingCourse ? `Editando: ${editingCourse.title}` : "Información Básica"}
                  </CardTitle>
                  {editingCourse && (
                    <CardDescription className="text-[#E50914] text-xs mt-1">
                      Modo edición — los cambios se guardarán al hacer clic en Actualizar
                    </CardDescription>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={resetCourseForm}
                  className="text-white/60 hover:text-white"
                  data-testid="cancel-course-form-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cerrar
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-white">Título del Curso</Label>
                  <Input
                    value={course.title}
                    onChange={(e) => setCourse({ ...course, title: e.target.value })}
                    required
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="Ej: Contabilidad Básica para Emprendedores"
                    data-testid="course-title-input"
                  />
                </div>

                <div>
                  <Label className="text-white">Descripción</Label>
                  <Textarea
                    value={course.description}
                    onChange={(e) => setCourse({ ...course, description: e.target.value })}
                    required
                    className="bg-white/5 border-white/10 text-white min-h-[100px]"
                    placeholder="Describe el curso..."
                    data-testid="course-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-white">Categoría</Label>
                    <Select value={course.category} onValueChange={(value) => setCourse({ ...course, category: value })}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="course-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Contabilidad">Contabilidad</SelectItem>
                        <SelectItem value="Tributación">Tributación</SelectItem>
                        <SelectItem value="Laboral">Laboral</SelectItem>
                        <SelectItem value="Finanzas">Finanzas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-white">Nivel</Label>
                    <Select value={course.level} onValueChange={(value) => setCourse({ ...course, level: value })}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="course-level-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Básico">Básico</SelectItem>
                        <SelectItem value="Intermedio">Intermedio</SelectItem>
                        <SelectItem value="Avanzado">Avanzado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-white">Tipo</Label>
                    <Select value={course.tier} onValueChange={(value) => setCourse({ ...course, tier: value })}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="course-tier-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GRATIS">GRATIS</SelectItem>
                        <SelectItem value="PRO">PRO</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-white">Duración</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={Math.floor(course.duration_hours)}
                          onChange={(e) => {
                            const h = parseInt(e.target.value) || 0;
                            const m = Math.round((course.duration_hours % 1) * 60);
                            setCourse({ ...course, duration_hours: h + m / 60 });
                          }}
                          placeholder="Horas"
                          className="bg-white/5 border-white/10 text-white"
                          data-testid="course-duration-hours-input"
                        />
                        <span className="text-xs text-white/50 mt-1 block">Horas</span>
                      </div>
                      <div className="flex-1">
                        <Input
                          type="number"
                          min="0"
                          max="59"
                          step="1"
                          value={Math.round((course.duration_hours % 1) * 60)}
                          onChange={(e) => {
                            const h = Math.floor(course.duration_hours);
                            const m = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                            setCourse({ ...course, duration_hours: h + m / 60 });
                          }}
                          placeholder="Minutos"
                          className="bg-white/5 border-white/10 text-white"
                          data-testid="course-duration-minutes-input"
                        />
                        <span className="text-xs text-white/50 mt-1 block">Minutos</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-white">Portada del curso</Label>
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={uploadCourseImage}
                      className="hidden"
                      id="course-image-file"
                      data-testid="course-image-file-input"
                    />
                    <Button
                      type="button"
                      onClick={() => document.getElementById("course-image-file").click()}
                      disabled={imageUploading}
                      className="bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                      data-testid="course-image-upload-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {imageUploading ? "Optimizando..." : "Subir imagen"}
                    </Button>
                    <span className="text-white/50 text-xs">
                      JPG, PNG o WebP · se convierte a WebP 1280×720 automáticamente
                    </span>
                  </div>

                  {course.image_url && (
                    <div className="flex flex-wrap items-start gap-4">
                      <img
                        src={resolveImageUrl(course.image_url)}
                        alt="Vista previa de la portada"
                        width={320}
                        height={180}
                        loading="lazy"
                        className="w-80 aspect-video object-cover rounded-lg border border-white/10"
                        data-testid="course-image-preview"
                      />
                      {imageInfo && (
                        <div className="text-white/60 text-xs space-y-1">
                          <p className="text-emerald-400 font-semibold">Imagen optimizada ✓</p>
                          <p>Tamaño final: {imageInfo.size_kb} KB (original {imageInfo.original_size_kb} KB)</p>
                          <p>Dimensiones: {imageInfo.width}×{imageInfo.height} px · WebP</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <Label className="text-white/70 text-xs">O pega una URL de imagen (opcional)</Label>
                    <Input
                      value={course.image_url}
                      onChange={(e) => setCourse({ ...course, image_url: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="https://..."
                      data-testid="course-image-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white">Orden de visualización</Label>
                  <Input
                    type="number"
                    min="0"
                    value={course.order}
                    onChange={(e) => setCourse({ ...course, order: parseInt(e.target.value) || 0 })}
                    className="bg-white/5 border-white/10 text-white max-w-[200px]"
                    placeholder="0"
                    data-testid="course-order-input"
                  />
                  <p className="text-xs text-white/50 mt-1">
                    Los cursos se muestran de menor a mayor. Usa 1, 2, 3... para ordenarlos en el catálogo.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Qué Aprenderás</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {course.what_you_will_learn.map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={item}
                      onChange={(e) => updateWhatYouWillLearn(index, e.target.value)}
                      className="bg-white/5 border-white/10 text-white flex-1"
                      placeholder="Ej: Cómo crear estados financieros"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeWhatYouWillLearn(index)}
                      className="border-white/20 text-white hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  onClick={addWhatYouWillLearn}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Item
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Requisitos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {course.requirements.map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={item}
                      onChange={(e) => updateRequirement(index, e.target.value)}
                      className="bg-white/5 border-white/10 text-white flex-1"
                      placeholder="Ej: Conocimientos básicos de matemáticas"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeRequirement(index)}
                      className="border-white/20 text-white hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  onClick={addRequirement}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Requisito
                </Button>
              </CardContent>
            </Card>

            {/* Materiales generales del curso */}
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#E50914]" />
                  Material General del Curso
                </CardTitle>
                <CardDescription className="text-white/60">
                  Archivos disponibles para todo el curso (no de una lección específica). Ej: temario, syllabus, libro completo.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {(course.course_materials || []).length === 0 ? (
                  <p className="text-white/40 text-sm italic">
                    Sin material general. Click en &quot;Agregar material&quot; para sumar PDFs, infografías o Excel.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {(course.course_materials || []).map((mat, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                        <Input
                          value={mat.title || ""}
                          onChange={(e) => updateCourseMaterial(idx, "title", e.target.value)}
                          placeholder="Título (ej: Syllabus del curso)"
                          className="col-span-4 bg-white/5 border-white/10 text-white text-sm"
                        />
                        <Input
                          value={mat.url || ""}
                          onChange={(e) => updateCourseMaterial(idx, "url", e.target.value)}
                          placeholder="URL (Google Drive, Dropbox)"
                          className="col-span-5 bg-white/5 border-white/10 text-white text-sm"
                        />
                        <Select
                          value={mat.type || "pdf"}
                          onValueChange={(v) => updateCourseMaterial(idx, "type", v)}
                        >
                          <SelectTrigger className="col-span-2 bg-white/5 border-white/10 text-white text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pdf">PDF</SelectItem>
                            <SelectItem value="excel">Excel</SelectItem>
                            <SelectItem value="word">Word</SelectItem>
                            <SelectItem value="image">Imagen</SelectItem>
                            <SelectItem value="zip">ZIP</SelectItem>
                            <SelectItem value="other">Otro</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removeCourseMaterial(idx)}
                          className="col-span-1 text-red-400 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={addCourseMaterial}
                  className="border-[#E50914]/40 text-[#E50914] hover:bg-[#E50914]/10"
                  data-testid="add-course-material-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar material
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Módulos y Lecciones</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {course.modules.map((module, moduleIndex) => (
                  <div key={module.id} className="border border-white/10 rounded-lg p-4 space-y-4">
                    <div className="flex gap-2">
                      <Input
                        value={module.title}
                        onChange={(e) => updateModule(moduleIndex, "title", e.target.value)}
                        className="bg-white/5 border-white/10 text-white flex-1"
                        placeholder="Título del Módulo"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeModule(moduleIndex)}
                        className="border-white/20 text-white hover:bg-red-500/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="space-y-2 ml-6">
                      {module.lessons.map((lesson, lessonIndex) => (
                        <div key={lesson.id} className="space-y-2 border-l-2 border-[#E50914] pl-4">
                          <div className="flex gap-2">
                            <Input
                              value={lesson.title}
                              onChange={(e) => updateLesson(moduleIndex, lessonIndex, "title", e.target.value)}
                              className="bg-white/5 border-white/10 text-white flex-1"
                              placeholder="Título de la Lección"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => removeLesson(moduleIndex, lessonIndex)}
                              className="border-white/20 text-white hover:bg-red-500/20"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              value={lesson.video_type === "mux" ? (lesson.mux_playback_id || "") : lesson.video_url}
                              onChange={(e) =>
                                updateLesson(
                                  moduleIndex,
                                  lessonIndex,
                                  lesson.video_type === "mux" ? "mux_playback_id" : "video_url",
                                  e.target.value
                                )
                              }
                              className="bg-white/5 border-white/10 text-white"
                              placeholder={lesson.video_type === "mux" ? "Playback ID de Mux" : "URL del Video"}
                              data-testid={`lesson-video-input-${moduleIndex}-${lessonIndex}`}
                            />
                            <Select
                              value={lesson.video_type}
                              onValueChange={(value) => updateLesson(moduleIndex, lessonIndex, "video_type", value)}
                            >
                              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid={`lesson-video-type-${moduleIndex}-${lessonIndex}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="youtube">YouTube</SelectItem>
                                <SelectItem value="vimeo">Vimeo</SelectItem>
                                <SelectItem value="mux">Mux (protegido)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {lesson.video_type === "mux" && (
                            <Select
                              value={lesson.mux_playback_id || ""}
                              onValueChange={(value) => updateLesson(moduleIndex, lessonIndex, "mux_playback_id", value)}
                            >
                              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid={`lesson-mux-picker-${moduleIndex}-${lessonIndex}`}>
                                <SelectValue placeholder="O elige un video de tu biblioteca de Mux" />
                              </SelectTrigger>
                              <SelectContent>
                                {muxVideos.filter((v) => v.playback_id).map((v) => (
                                  <SelectItem key={v.id} value={v.playback_id}>{v.title}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <Input
                            type="number"
                            value={lesson.duration_minutes}
                            onChange={(e) =>
                              updateLesson(moduleIndex, lessonIndex, "duration_minutes", parseInt(e.target.value))
                            }
                            className="bg-white/5 border-white/10 text-white"
                            placeholder="Duración (minutos)"
                          />

                          {/* Materiales descargables */}
                          <div className="bg-white/[0.02] border border-white/10 rounded-md p-3 mt-2">
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-white/80 text-xs flex items-center gap-2">
                                <FileText className="w-3 h-3 text-[#E50914]" />
                                Materiales descargables (PDFs, infografías, Excel)
                              </Label>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => addMaterial(moduleIndex, lessonIndex)}
                                className="text-[#E50914] hover:text-white hover:bg-[#E50914]/20 h-7 text-xs"
                                data-testid={`add-material-${moduleIndex}-${lessonIndex}`}
                              >
                                <Plus className="w-3 h-3 mr-1" /> Agregar material
                              </Button>
                            </div>
                            {(lesson.materials || []).length === 0 ? (
                              <p className="text-white/40 text-xs italic">
                                Sin materiales. Click en &quot;Agregar material&quot; para subir PDFs, infografías o Excel mediante link.
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {(lesson.materials || []).map((mat, matIndex) => (
                                  <div key={matIndex} className="grid grid-cols-12 gap-2 items-start">
                                    <Input
                                      value={mat.title || ""}
                                      onChange={(e) => updateMaterial(moduleIndex, lessonIndex, matIndex, "title", e.target.value)}
                                      placeholder="Título del material (ej: Infografía IVA)"
                                      className="col-span-4 bg-white/5 border-white/10 text-white h-9 text-xs"
                                    />
                                    <Input
                                      value={mat.url || ""}
                                      onChange={(e) => updateMaterial(moduleIndex, lessonIndex, matIndex, "url", e.target.value)}
                                      placeholder="URL (Google Drive, Dropbox, link directo)"
                                      className="col-span-5 bg-white/5 border-white/10 text-white h-9 text-xs"
                                    />
                                    <Select
                                      value={mat.type || "pdf"}
                                      onValueChange={(v) => updateMaterial(moduleIndex, lessonIndex, matIndex, "type", v)}
                                    >
                                      <SelectTrigger className="col-span-2 bg-white/5 border-white/10 text-white h-9 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="pdf">PDF</SelectItem>
                                        <SelectItem value="excel">Excel</SelectItem>
                                        <SelectItem value="word">Word</SelectItem>
                                        <SelectItem value="image">Imagen</SelectItem>
                                        <SelectItem value="zip">ZIP</SelectItem>
                                        <SelectItem value="other">Otro</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <Button
                                      type="button"
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => removeMaterial(moduleIndex, lessonIndex, matIndex)}
                                      className="col-span-1 text-red-400 hover:bg-red-500/20 h-9"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <p className="text-white/30 text-[10px] mt-2">
                              Tip: En Google Drive, click derecho al archivo → &quot;Obtener enlace&quot; → asegúrate que diga &quot;Cualquier persona con el enlace&quot;.
                            </p>
                          </div>
                        </div>
                      ))}
                      <Button
                        type="button"
                        onClick={() => addLesson(moduleIndex)}
                        variant="outline"
                        size="sm"
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar Lección
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  onClick={addModule}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Módulo
                </Button>
              </CardContent>
            </Card>

            <Button
              type="submit"
              disabled={courseLoading}
              className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold h-12"
              data-testid="create-course-btn"
            >
              <Save className="w-5 h-5 mr-2" />
              {courseLoading
                ? (editingCourse ? "Actualizando..." : "Creando...")
                : (editingCourse ? "Actualizar Curso" : "Crear Curso")}
            </Button>
            </form>
            )}
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === "templates" && (
          <div className="space-y-6">
            {/* Template Form */}
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  {editingTemplate ? "Editar Plantilla" : "Nueva Plantilla"}
                  {editingTemplate && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={cancelEdit}
                      className="text-white/60 hover:text-white"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancelar
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleTemplateSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white">Título</Label>
                      <Input
                        value={template.title}
                        onChange={(e) => setTemplate({ ...template, title: e.target.value })}
                        required
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Ej: Libro Diario Excel"
                        data-testid="template-title-input"
                      />
                    </div>
                    <div>
                      <Label className="text-white">Categoría</Label>
                      <Select value={template.category} onValueChange={(value) => setTemplate({ ...template, category: value })}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="template-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Contabilidad">Contabilidad</SelectItem>
                          <SelectItem value="Tributación">Tributación</SelectItem>
                          <SelectItem value="Laboral">Laboral</SelectItem>
                          <SelectItem value="Finanzas">Finanzas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white">Descripción</Label>
                    <Textarea
                      value={template.description}
                      onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                      required
                      className="bg-white/5 border-white/10 text-white min-h-[80px]"
                      placeholder="Describe la plantilla..."
                      data-testid="template-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white">Tipo de Archivo</Label>
                      <Select value={template.type} onValueChange={(value) => setTemplate({ ...template, type: value })}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="template-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="excel">Excel</SelectItem>
                          <SelectItem value="pdf">PDF</SelectItem>
                          <SelectItem value="word">Word</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white">URL del Archivo</Label>
                      <Input
                        value={template.url}
                        onChange={(e) => setTemplate({ ...template, url: e.target.value })}
                        required
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="https://docs.google.com/spreadsheets/..."
                        data-testid="template-url-input"
                      />
                    </div>
                    <div>
                      <Label className="text-white">Orden</Label>
                      <Input
                        type="number"
                        min="0"
                        value={template.order || 0}
                        onChange={(e) => setTemplate({ ...template, order: parseInt(e.target.value) || 0 })}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="0"
                        data-testid="template-order-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={templateLoading}
                    className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold h-12"
                    data-testid="save-template-btn"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {templateLoading ? "Guardando..." : editingTemplate ? "Actualizar Plantilla" : "Crear Plantilla"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Templates List */}
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Plantillas Existentes ({templates.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {templates.length === 0 ? (
                  <p className="text-white/60 text-center py-8">No hay plantillas creadas aún</p>
                ) : (
                  <div className="space-y-3">
                    {templates.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10"
                        data-testid={`template-item-${t.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5 text-[#E50914]" />
                            <h3 className="text-white font-semibold">{t.title}</h3>
                            <span className="text-xs px-2 py-1 bg-white/10 rounded text-white/60">{t.category}</span>
                            <span className="text-xs px-2 py-1 bg-[#E50914]/20 rounded text-[#E50914]">{t.type.toUpperCase()}</span>
                          </div>
                          <p className="text-white/60 text-sm mt-1 line-clamp-1">{t.description}</p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => editTemplate(t)}
                            className="border-white/20 text-white hover:bg-white/10"
                            data-testid={`edit-template-${t.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteTemplate(t.id)}
                            className="border-white/20 text-white hover:bg-red-500/20"
                            data-testid={`delete-template-${t.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === "subscriptions" && (
          <div className="space-y-6">
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#E50914]" />
                  Suscriptores ({subscriptions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {subscriptionsLoading ? (
                  <p className="text-white/60 text-center py-8">Cargando suscriptores...</p>
                ) : subscriptions.length === 0 ? (
                  <p className="text-white/60 text-center py-8">No hay suscriptores aún</p>
                ) : (
                  <div className="space-y-4">
                    {subscriptions.map((sub) => (
                      <div
                        key={sub.user_id}
                        className="p-4 bg-white/5 rounded-lg border border-white/10"
                        data-testid={`subscription-item-${sub.user_id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Users className="w-5 h-5 text-[#E50914]" />
                              <h3 className="text-white font-semibold">{sub.full_name || "Usuario"}</h3>
                              <span className={`text-xs px-2 py-1 rounded ${
                                sub.subscription_status === "pro" 
                                  ? "bg-green-500/20 text-green-400"
                                  : "bg-white/10 text-white/60"
                              }`}>
                                {sub.subscription_status === "pro" ? "PRO" : "Gratuito"}
                              </span>
                              {sub.plan && (
                                <span className="text-xs px-2 py-1 bg-[#E50914]/20 rounded text-[#E50914]">
                                  {sub.plan === "monthly" ? "Mensual $5" : sub.plan === "annual" ? "Anual $50" : sub.plan}
                                </span>
                              )}
                            </div>
                            <p className="text-white/60 text-sm">{sub.email}</p>
                            <p className="text-white/40 text-xs mt-1">
                              Registrado: {sub.created_at ? new Date(sub.created_at).toLocaleDateString('es-EC') : "—"}
                              {sub.subscription_end_date && (
                                <span> | Vence: {new Date(sub.subscription_end_date).toLocaleDateString('es-EC')}</span>
                              )}
                            </p>
                          </div>
                          
                          {/* Billing Info */}
                          {sub.billing && Object.keys(sub.billing).length > 0 && (
                          <div className="bg-white/5 rounded-lg p-3 text-sm space-y-1 min-w-[280px]">
                            <h4 className="text-white font-semibold text-xs uppercase tracking-wider mb-2">Datos de Facturacion</h4>
                            <p className="text-white/70"><span className="text-white/40">RUC/CI:</span> {sub.billing.ruc_ci || "—"}</p>
                            <p className="text-white/70"><span className="text-white/40">Email:</span> {sub.billing.email || "—"}</p>
                            <p className="text-white/70"><span className="text-white/40">Direccion:</span> {sub.billing.address || "—"}</p>
                            <p className="text-white/70"><span className="text-white/40">Telefono:</span> {sub.billing.phone || "—"}</p>
                          </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Downloads Tab */}
        {activeTab === "downloads" && (
          <div className="space-y-6" data-testid="downloads-tab-content">
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#E50914]" />
                  Consumo de Plantillas ({usageData.month})
                </CardTitle>
                <CardDescription className="text-white/60">
                  Límite global: <span className="text-white font-semibold">{usageData.global_limit}</span> plantillas diferentes por mes.
                  Re-descargar la misma plantilla no consume cupo. El cupo se reinicia el día 1 de cada mes y no se acumula.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {usageLoading ? (
                  <p className="text-white/60 text-center py-8">Cargando consumo...</p>
                ) : usageData.users.length === 0 ? (
                  <p className="text-white/60 text-center py-8">No hay usuarios registrados</p>
                ) : (
                  <div className="space-y-3">
                    {usageData.users.map((u) => {
                      const edit = quotaEdits[u.user_id] || { limit_override: "", extra_downloads: "0" };
                      const pct = u.limit > 0 ? Math.min(100, (u.used / u.limit) * 100) : 0;
                      return (
                        <div
                          key={u.user_id}
                          className="p-4 bg-white/5 rounded-lg border border-white/10"
                          data-testid={`usage-row-${u.user_id}`}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-white font-semibold truncate">{u.full_name || "Usuario"}</p>
                                {u.is_annual_subscriber ? (
                                  <Badge className="bg-green-500/20 text-green-400 border-0">Anual activo</Badge>
                                ) : (
                                  <Badge className="bg-white/10 text-white/60 border-0">
                                    {u.plan === "monthly" ? "Mensual" : u.plan || "Sin plan"}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-white/50 text-sm truncate">{u.email}</p>
                              <p className="text-white/80 text-sm mt-2" data-testid={`usage-count-${u.user_id}`}>
                                {u.used} de {u.limit} descargas utilizadas este mes
                                {u.extra_downloads > 0 && (
                                  <span className="text-emerald-400"> (+{u.extra_downloads} extras)</span>
                                )}
                              </p>
                              <div className="mt-1 h-2 bg-black/40 rounded-full overflow-hidden max-w-sm">
                                <div className="h-full bg-[#E50914] transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </div>

                            <div className="flex flex-wrap items-end gap-3">
                              <div className="w-32">
                                <Label className="text-white/60 text-xs mb-1 block">Límite personalizado</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  placeholder={`Global (${usageData.global_limit})`}
                                  value={edit.limit_override}
                                  onChange={(e) =>
                                    setQuotaEdits({
                                      ...quotaEdits,
                                      [u.user_id]: { ...edit, limit_override: e.target.value },
                                    })
                                  }
                                  className="bg-white/5 border-white/10 text-white"
                                  data-testid={`quota-limit-input-${u.user_id}`}
                                />
                              </div>
                              <div className="w-32">
                                <Label className="text-white/60 text-xs mb-1 block">Extras (este mes)</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={edit.extra_downloads}
                                  onChange={(e) =>
                                    setQuotaEdits({
                                      ...quotaEdits,
                                      [u.user_id]: { ...edit, extra_downloads: e.target.value },
                                    })
                                  }
                                  className="bg-white/5 border-white/10 text-white"
                                  data-testid={`quota-extras-input-${u.user_id}`}
                                />
                              </div>
                              <Button
                                onClick={() => saveQuota(u.user_id)}
                                disabled={quotaSavingId === u.user_id}
                                className="bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                                data-testid={`quota-save-btn-${u.user_id}`}
                              >
                                <Save className="w-4 h-4 mr-2" />
                                {quotaSavingId === u.user_id ? "Guardando..." : "Guardar"}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => loadDownloadHistory(u.user_id)}
                                className="border-white/20 text-white hover:bg-white/10"
                                data-testid={`quota-history-btn-${u.user_id}`}
                              >
                                <History className="w-4 h-4 mr-2" />
                                Historial
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-[#E50914]" />
                    Historial de Descargas ({downloadHistory.length})
                  </CardTitle>
                  <CardDescription className="text-white/60">
                    {historyUserId ? "Filtrado por usuario seleccionado" : "Todas las descargas recientes"}
                  </CardDescription>
                </div>
                {historyUserId && (
                  <Button
                    variant="outline"
                    onClick={() => loadDownloadHistory(null)}
                    className="border-white/20 text-white hover:bg-white/10"
                    data-testid="history-clear-filter-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Ver todos
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {downloadHistory.length === 0 ? (
                  <p className="text-white/60 text-center py-8">No hay descargas registradas</p>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {downloadHistory.map((d, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between gap-4 p-3 bg-white/5 rounded-lg border border-white/10"
                        data-testid={`download-history-item-${i}`}
                      >
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">{d.template_title}</p>
                          <p className="text-white/50 text-xs truncate">
                            {d.user_name || d.user_email || d.user_id}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-white/60 text-xs">
                            {d.downloaded_at ? new Date(d.downloaded_at).toLocaleString("es-EC") : "—"}
                          </p>
                          {d.counted === false && (
                            <span className="text-[10px] text-emerald-400">re-descarga (sin cupo)</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Videos (Mux) Tab */}
        {activeTab === "videos" && <MuxVideoLibrary />}

        {/* Promotions Tab */}
        {activeTab === "promotions" && <PromotionsModule />}

        {/* Email Tab */}
        {activeTab === "email" && (
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleSendTestEmail}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="send-test-email-btn"
              >
                <TestTube className="w-4 h-4 mr-2" />
                Enviar Email de Prueba
              </Button>
              <Button
                onClick={handleSendReminders}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="send-reminders-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                Enviar Recordatorios de Vencimiento
              </Button>
            </div>

            {/* Campaign Editor */}
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#E50914]" />
                  Nueva Campana de Email
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Asunto</Label>
                    <Input
                      value={campaignSubject}
                      onChange={(e) => setCampaignSubject(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="Ej: Nuevo curso disponible"
                      data-testid="campaign-subject-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Segmento</Label>
                    <Select value={campaignSegment} onValueChange={setCampaignSegment}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="campaign-segment-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los usuarios</SelectItem>
                        <SelectItem value="pro">Solo PRO</SelectItem>
                        <SelectItem value="free">Solo Gratuitos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-white mb-2 block">Contenido (HTML)</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => insertFormatting("bold")} className="border-white/20 text-white hover:bg-white/10 text-xs h-8 px-3">
                      <strong>B</strong>
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => insertFormatting("italic")} className="border-white/20 text-white hover:bg-white/10 text-xs h-8 px-3">
                      <em>I</em>
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => insertFormatting("h2")} className="border-white/20 text-white hover:bg-white/10 text-xs h-8 px-3">
                      H2
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => insertFormatting("link")} className="border-white/20 text-white hover:bg-white/10 text-xs h-8 px-3">
                      Link
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => insertFormatting("button")} className="border-white/20 text-white hover:bg-white/10 text-xs h-8 px-3">
                      Boton
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => insertFormatting("br")} className="border-white/20 text-white hover:bg-white/10 text-xs h-8 px-3">
                      Salto
                    </Button>
                  </div>
                  <textarea
                    ref={editorRef}
                    value={campaignBody}
                    onChange={(e) => setCampaignBody(e.target.value)}
                    className="w-full min-h-[200px] bg-white/5 border border-white/10 rounded-md p-3 text-white font-mono text-sm focus:outline-none focus:border-[#E50914]/50 resize-y"
                    placeholder="Escribe el contenido HTML del email...&#10;Usa los botones arriba para insertar formato.&#10;Variables disponibles: El nombre del usuario se inserta automaticamente."
                    data-testid="campaign-body-editor"
                  />
                </div>

                {campaignBody && (
                  <div>
                    <Label className="text-white mb-2 block">Vista Previa</Label>
                    <div className="bg-[#141414] border border-white/10 rounded-lg p-4 max-h-[300px] overflow-auto">
                      <div className="text-white/80 text-sm" dangerouslySetInnerHTML={{ __html: campaignBody }} />
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleSendCampaign}
                  disabled={campaignLoading || !campaignSubject.trim() || !campaignBody.trim()}
                  className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold h-12"
                  data-testid="send-campaign-btn"
                >
                  <Send className="w-5 h-5 mr-2" />
                  {campaignLoading ? "Enviando..." : "Enviar Campana"}
                </Button>
              </CardContent>
            </Card>

            {/* Campaigns History */}
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Historial de Campanas ({campaigns.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {campaigns.length === 0 ? (
                  <p className="text-white/60 text-center py-6">No hay campanas enviadas</p>
                ) : (
                  <div className="space-y-3">
                    {campaigns.map((c) => (
                      <div key={c.id} className="p-4 bg-white/5 rounded-lg border border-white/10" data-testid={`campaign-item-${c.id}`}>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Mail className="w-4 h-4 text-[#E50914]" />
                              <h3 className="text-white font-semibold text-sm">{c.subject}</h3>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                c.status === "completed" ? "bg-green-500/20 text-green-400"
                                : c.status === "sending" ? "bg-yellow-500/20 text-yellow-400"
                                : "bg-red-500/20 text-red-400"
                              }`}>
                                {c.status === "completed" ? "Enviada" : c.status === "sending" ? "Enviando..." : c.status}
                              </span>
                              <span className="text-xs px-2 py-0.5 bg-white/10 rounded text-white/60">
                                {c.segment === "all" ? "Todos" : c.segment.toUpperCase()}
                              </span>
                            </div>
                            <p className="text-white/40 text-xs mt-1">
                              {new Date(c.created_at).toLocaleString('es-EC')} | Enviados: {c.sent_count} | Fallidos: {c.failed_count}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Email Logs */}
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>Registro de Emails ({emailLogs.length})</span>
                  <div className="flex gap-2">
                    <Select value={emailLogFilter} onValueChange={(v) => { setEmailLogFilter(v); }}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white w-40 h-8 text-xs" data-testid="email-log-filter">
                        <SelectValue placeholder="Filtrar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_types">Todos</SelectItem>
                        <SelectItem value="welcome">Bienvenida</SelectItem>
                        <SelectItem value="subscription">Suscripcion</SelectItem>
                        <SelectItem value="reminder">Recordatorio</SelectItem>
                        <SelectItem value="campaign">Campana</SelectItem>
                        <SelectItem value="test">Prueba</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={loadEmailLogs} className="border-white/20 text-white hover:bg-white/10 h-8">
                      Actualizar
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {emailLogsLoading ? (
                  <p className="text-white/60 text-center py-6">Cargando...</p>
                ) : emailLogs.length === 0 ? (
                  <p className="text-white/60 text-center py-6">No hay registros de email</p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-auto">
                    {emailLogs.map((log, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded border border-white/5 text-sm">
                        {log.status === "sent" ? (
                          <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        ) : log.status === "pending" ? (
                          <Clock className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white truncate">{log.subject}</p>
                          <p className="text-white/40 text-xs truncate">{log.recipient}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            log.email_type === "welcome" ? "bg-blue-500/20 text-blue-400"
                            : log.email_type === "subscription" ? "bg-purple-500/20 text-purple-400"
                            : log.email_type === "campaign" ? "bg-orange-500/20 text-orange-400"
                            : log.email_type === "reminder" ? "bg-yellow-500/20 text-yellow-400"
                            : "bg-white/10 text-white/60"
                          }`}>
                            {log.email_type}
                          </span>
                          <p className="text-white/30 text-xs mt-1">{new Date(log.created_at).toLocaleString('es-EC')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="space-y-6">
            <Card className="bg-card/50 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#E50914]" />
                  Configuración de Plantillas
                </CardTitle>
                <CardDescription className="text-white/60">
                  Define cuántas plantillas diferentes pueden descargar los suscriptores PRO Anual cada mes.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-white mb-2 block">
                    Límite mensual de descargas por usuario
                  </Label>
                  <div className="flex gap-2 max-w-xs">
                    <Input
                      type="number"
                      min="0"
                      max="1000"
                      value={settings.monthly_template_download_limit}
                      onChange={(e) =>
                        setSettings({ ...settings, monthly_template_download_limit: parseInt(e.target.value) || 0 })
                      }
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="settings-download-limit-input"
                    />
                    <Button
                      onClick={handleSaveSettings}
                      disabled={settingsLoading}
                      className="bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                      data-testid="settings-save-btn"
                    >
                      {settingsLoading ? "Guardando..." : "Guardar"}
                    </Button>
                  </div>
                  <p className="text-white/40 text-xs mt-2">
                    Solo los usuarios con Plan PRO Anual pueden descargar plantillas. Los planes Mensual y Gratis no tienen acceso.
                  </p>
                </div>

                <div className="border-t border-white/10 pt-4">
                  <p className="text-white/80 text-sm font-semibold mb-2">Reglas activas:</p>
                  <ul className="space-y-1 text-white/60 text-sm">
                    <li>• Plan GRATIS → ❌ Sin acceso a plantillas</li>
                    <li>• Plan PRO Mensual ($10/mes) → ❌ Sin acceso a plantillas</li>
                    <li>• Plan PRO Anual ($100/año) → ✅ Acceso a {settings.monthly_template_download_limit} plantillas diferentes/mes</li>
                    <li>• Re-descargar una plantilla ya descargada en el mes NO consume cupo</li>
                    <li>• Contador se reinicia automáticamente cada mes (día 1) y no se acumula</li>
                    <li>• Puedes dar límites personalizados o extras en la pestaña "Descargas"</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Timer de Descuento */}
            <Card className="bg-card/50 backdrop-blur-md border-white/10" data-testid="promotion-card">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Timer className="w-5 h-5 text-[#E50914]" />
                  Timer de Descuento (Promoción)
                </CardTitle>
                <CardDescription className="text-white/60">
                  Activa una oferta con contador regresivo en la página de ventas. El descuento se aplica automáticamente en el checkout.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Estado actual */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${promotion.is_live ? "bg-green-500 animate-pulse" : "bg-white/30"}`} />
                    <div>
                      <p className="text-white text-sm font-semibold">
                        {promotion.is_live ? "Promoción EN VIVO" : promotion.active ? "Programada / Expirada" : "Desactivada"}
                      </p>
                      {promotion.is_live && (
                        <p className="text-white/60 text-xs">
                          Termina en: <span className="font-mono text-[#E50914]">{formatCountdown(promotion.seconds_left)}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={handleTogglePromotion}
                    disabled={promotionLoading}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                    data-testid="promotion-toggle-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    {promotion.active ? "Desactivar" : "Activar"}
                  </Button>
                </div>

                {/* Textos */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white mb-2 block">Título del banner</Label>
                    <Input
                      value={promotion.title || ""}
                      onChange={(e) => setPromotion({ ...promotion, title: e.target.value })}
                      placeholder="¡Oferta por tiempo limitado!"
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="promotion-title-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">
                      Descuento (%) <Percent className="w-3 h-3 inline text-[#E50914]" />
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="99"
                      value={promotion.discount_percentage}
                      onChange={(e) => setPromotion({ ...promotion, discount_percentage: parseInt(e.target.value) || 0 })}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="promotion-discount-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white mb-2 block">Descripción</Label>
                  <Textarea
                    value={promotion.description || ""}
                    onChange={(e) => setPromotion({ ...promotion, description: e.target.value })}
                    placeholder="Aprovecha este descuento exclusivo antes de que termine."
                    className="bg-white/5 border-white/10 text-white min-h-[70px]"
                    data-testid="promotion-description-input"
                  />
                </div>

                {/* Aplica a */}
                <div>
                  <Label className="text-white mb-2 block">Aplicar a planes</Label>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                      <Switch
                        checked={!!promotion.applies_to_monthly}
                        onCheckedChange={(v) => setPromotion({ ...promotion, applies_to_monthly: v })}
                        data-testid="promotion-monthly-switch"
                      />
                      <span className="text-white text-sm">Plan Mensual ($10/mes)</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                      <Switch
                        checked={!!promotion.applies_to_annual}
                        onCheckedChange={(v) => setPromotion({ ...promotion, applies_to_annual: v })}
                        data-testid="promotion-annual-switch"
                      />
                      <span className="text-white text-sm">Plan Anual ($100/año)</span>
                    </div>
                  </div>
                </div>

                {/* Modo de tiempo */}
                <div>
                  <Label className="text-white mb-2 block">Duración</Label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center gap-3 mb-3">
                        <input
                          type="radio"
                          id="mode-hours"
                          name="time_mode"
                          checked={promotion.time_mode === "hours"}
                          onChange={() => setPromotion({ ...promotion, time_mode: "hours" })}
                          data-testid="promotion-mode-hours"
                          className="accent-[#E50914]"
                        />
                        <label htmlFor="mode-hours" className="text-white text-sm font-semibold cursor-pointer">
                          Por horas (desde ahora)
                        </label>
                      </div>
                      <Input
                        type="number"
                        min="1"
                        max="720"
                        value={promotion.duration_hours}
                        onChange={(e) => setPromotion({ ...promotion, duration_hours: parseInt(e.target.value) || 1 })}
                        disabled={promotion.time_mode !== "hours"}
                        className="bg-white/5 border-white/10 text-white disabled:opacity-40"
                        placeholder="24"
                        data-testid="promotion-duration-hours-input"
                      />
                      <p className="text-white/40 text-xs mt-1">Al activar, contará desde el momento de guardado.</p>
                    </div>

                    <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center gap-3 mb-3">
                        <input
                          type="radio"
                          id="mode-dates"
                          name="time_mode"
                          checked={promotion.time_mode === "dates"}
                          onChange={() => setPromotion({ ...promotion, time_mode: "dates" })}
                          data-testid="promotion-mode-dates"
                          className="accent-[#E50914]"
                        />
                        <label htmlFor="mode-dates" className="text-white text-sm font-semibold cursor-pointer">
                          Por fechas específicas
                        </label>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-white/70 text-xs mb-1 block">Inicio</Label>
                          <Input
                            type="datetime-local"
                            value={promotion.starts_at_local || ""}
                            onChange={(e) => setPromotion({ ...promotion, starts_at_local: e.target.value })}
                            disabled={promotion.time_mode !== "dates"}
                            className="bg-white/5 border-white/10 text-white disabled:opacity-40"
                            data-testid="promotion-starts-at-input"
                          />
                        </div>
                        <div>
                          <Label className="text-white/70 text-xs mb-1 block">Fin</Label>
                          <Input
                            type="datetime-local"
                            value={promotion.ends_at_local || ""}
                            onChange={(e) => setPromotion({ ...promotion, ends_at_local: e.target.value })}
                            disabled={promotion.time_mode !== "dates"}
                            className="bg-white/5 border-white/10 text-white disabled:opacity-40"
                            data-testid="promotion-ends-at-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activar */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#E50914]/10 border border-[#E50914]/30">
                  <Switch
                    checked={!!promotion.active}
                    onCheckedChange={(v) => setPromotion({ ...promotion, active: v })}
                    data-testid="promotion-active-switch"
                  />
                  <div className="flex-1">
                    <p className="text-white text-sm font-semibold">Promoción activa</p>
                    <p className="text-white/60 text-xs">
                      Debe estar activa y dentro del rango de fechas para mostrarse.
                    </p>
                  </div>
                </div>

                {/* Preview de precio */}
                {promotion.discount_percentage > 0 && (
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-sm">
                    <p className="text-white/60 mb-2">Vista previa de precios con {promotion.discount_percentage}% OFF:</p>
                    <div className="flex flex-wrap gap-4">
                      {promotion.applies_to_monthly && (
                        <div className="text-white">
                          <span className="text-white/40 line-through mr-2">$10.00</span>
                          <span className="text-[#E50914] font-bold">
                            ${(10 * (100 - promotion.discount_percentage) / 100).toFixed(2)}
                          </span>
                          <span className="text-white/60 text-xs ml-1">/mes</span>
                        </div>
                      )}
                      {promotion.applies_to_annual && (
                        <div className="text-white">
                          <span className="text-white/40 line-through mr-2">$100.00</span>
                          <span className="text-[#E50914] font-bold">
                            ${(100 * (100 - promotion.discount_percentage) / 100).toFixed(2)}
                          </span>
                          <span className="text-white/60 text-xs ml-1">/año</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleSavePromotion}
                  disabled={promotionLoading}
                  className="bg-[#E50914] hover:bg-[#E50914]/90 text-white"
                  data-testid="promotion-save-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {promotionLoading ? "Guardando..." : "Guardar promoción"}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
